import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/utils/videoUtils";
import { formatCount } from "@/utils/format";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setActive(((index % videos.length) + videos.length) % videos.length);
  }, [videos.length]);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % videos.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [videos.length]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = (event.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    if (delta < -50) next();
    if (delta > 50) prev();
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden rounded-lg bg-background-950"
      aria-label="精选短视频轮播"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[420px] w-full md:h-[520px]">
        {videos.map((video, index) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={video.hero}
              alt={`${video.title} 精选短视频封面`}
              title={`${video.title} - 91抖音精选`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          </div>
        ))}

        <div className="absolute inset-0 flex flex-col justify-end px-5 pb-14 md:px-12 md:pb-16">
          {videos.map((video, index) => (
            <div
              key={video.id}
              className={`transition-all duration-500 ${
                index === active ? "block animate-fade-up" : "hidden"
              }`}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-fill"></i>
                {video.categoryName} · 精选推荐
              </span>
              <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold leading-tight text-white md:text-4xl">
                {video.title}
              </h2>
              <p className="mt-3 hidden max-w-xl text-sm text-white/80 sm:block">
                {video.description}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${video.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <div className="flex items-center gap-4 text-xs text-white/80">
                  <span className="flex items-center gap-1">
                    <i className="ri-play-circle-line text-base"></i>
                    {formatCount(video.plays)} 次播放
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-3-line text-base"></i>
                    {formatCount(video.likes)} 点赞
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={prev}
          aria-label="上一个"
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition-colors hover:bg-black/55 md:left-5 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一个"
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white transition-colors hover:bg-black/55 md:right-5 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {videos.map((video, index) => (
            <button
              key={video.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-6 bg-primary-500" : "w-1.5 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}